import type {
  AriValueSource,
  DynamicPricingCalendarDay,
  DynamicPricingOfferingState,
} from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';
import i18n from '@/shared/i18n';

export const offeringLabel = (state: DynamicPricingOfferingState) =>
  i18n.t(`admin.dynamicPricing.calendar.offeringStates.${state}`);

export const calculationLabel = (status: string) =>
  i18n.t(`admin.dynamicPricing.calendar.calculationStatuses.${status}`, {
    defaultValue: status,
  });

export const publicationLabel = (status: string) =>
  i18n.t(`admin.dynamicPricing.calendar.publicationStatuses.${status}`, {
    defaultValue: status,
  });

export const publicationSymbol = (status: string) =>
  status === 'Confirmed' ? '✓' : status === 'Failed' || status === 'ConfirmationFailed' ? '!' : '↗';

export const sourceLabel = (source: AriValueSource) =>
  i18n.t(`admin.dynamicPricing.calendar.sources.${source}`);

export function dateIndex(from: string, value: string) {
  return Math.round(
    (Date.parse(`${value}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  );
}

export function dayLabel(apartmentName: string, day: DynamicPricingCalendarDay, currency: string) {
  const display = 'admin.dynamicPricing.calendar.display';
  const currentRate = day.currentRate ?? null;
  const currentMinimumStay = day.currentMinimumStay ?? null;
  const facts = [
    apartmentName,
    new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium' }).format(
      new Date(`${day.date}T00:00:00Z`),
    ),
    offeringLabel(day.offeringState),
    currentRate === null
      ? i18n.t(`${display}.noCurrentRate`)
      : i18n.t(`${display}.currentRate`, { value: price(currentRate, currency) }),
    day.confirmedRate === null
      ? i18n.t(`${display}.noConfirmedRate`)
      : i18n.t(`${display}.confirmedRate`, { value: price(day.confirmedRate, currency) }),
  ];
  if (day.desiredRate !== null && day.desiredRate !== currentRate) {
    facts.push(i18n.t(`${display}.desiredRate`, { value: price(day.desiredRate, currency) }));
  }
  facts.push(
    currentMinimumStay === null
      ? i18n.t(`${display}.noCurrentMinimumStay`)
      : i18n.t(`${display}.currentMinimumStay`, { value: currentMinimumStay }),
    day.confirmedMinimumStay === null
      ? i18n.t(`${display}.noConfirmedMinimumStay`)
      : i18n.t(`${display}.confirmedMinimumStay`, { value: day.confirmedMinimumStay }),
  );
  if (day.desiredMinimumStay !== null && day.desiredMinimumStay !== currentMinimumStay) {
    facts.push(i18n.t(`${display}.targetMinimumStay`, { value: day.desiredMinimumStay }));
  }
  if (day.rateSource) {
    facts.push(i18n.t(`${display}.rateOwner`, { source: sourceLabel(day.rateSource) }));
  }
  if (day.minimumStaySource) {
    facts.push(
      i18n.t(`${display}.minimumStayOwner`, { source: sourceLabel(day.minimumStaySource) }),
    );
  }
  if (day.calculationStatus) {
    facts.push(
      i18n.t(`${display}.calculation`, { status: calculationLabel(day.calculationStatus) }),
    );
  }
  if (day.publication) {
    facts.push(
      i18n.t(`${display}.publication`, { status: publicationLabel(day.publication.status) }),
    );
    if (day.publication.lastErrorCode) {
      facts.push(i18n.t(`${display}.publicationNeedsAttention`));
    }
  }
  return facts.join(', ');
}

export function price(value: number | null, currency: string) {
  return value === null ? '—' : formatMoney(value, currency);
}

export const stay = (value: number | null) =>
  value === null ? '—' : i18n.t('admin.dynamicPricing.calendar.display.nights', { count: value });
export const signed = (value: number) => `${value > 0 ? '+' : ''}${value}`;
